/**
 * Job-status metadata readers for the import progress panel.
 *
 * Extracted VERBATIM from `ImportProgressIndicator.tsx` (only the surrounding
 * comments were kept) so that file stays under the 500-line LLM ceiling once
 * completion became observation-driven. No behaviour changed.
 */

import type { UnknownRowsTicketBanner } from '@/modules/accounting/features/import/components/custom-importer/sections/ResultsPreviewSection'
import { ApiException } from '@/shared/utils/api'

/**
 * Issue #311 defense-in-depth: track jobIds whose status fetch already
 * returned `404 JOB_NOT_FOUND`. The backend fix (sync-stream.handlers.ts)
 * stops replaying historical `sync_completed` events to newly-connected
 * SSE clients, but in case of a regression — or a race where a job is
 * deleted between SSE emission and the client's refetch — we cache the
 * outcome here so we never re-fetch a known-missing jobId from this tab.
 *
 * Module-level so the cache persists across re-renders of the indicator
 * (the component remounts on layout transitions). Bounded to 500 entries
 * to keep the set from growing without limit during a long-lived session.
 */
const knownMissingJobIds = new Set<string>()
const KNOWN_MISSING_JOB_IDS_LIMIT = 500

export function isJobKnownMissing(jobId: string): boolean {
  return knownMissingJobIds.has(jobId)
}

export function markJobAsMissing(jobId: string): void {
  if (knownMissingJobIds.size >= KNOWN_MISSING_JOB_IDS_LIMIT) {
    // Drop oldest insertion (Set preserves insertion order). Cheap O(n)
    // truncation is fine — we only hit this after 500 distinct stale IDs
    // in a single session, which is already pathological.
    const firstKey = knownMissingJobIds.values().next().value
    if (firstKey !== undefined) {
      knownMissingJobIds.delete(firstKey)
    }
  }
  knownMissingJobIds.add(jobId)
}

export function isJobNotFoundException(error: unknown): boolean {
  if (!(error instanceof ApiException)) return false
  return error.status === 404 || error.code === 'JOB_NOT_FOUND'
}

/**
 * Narrow shape of the `ImportJob` payload returned by `getJobStatus`. The
 * service typedefs the field as `Record<string, unknown>` so we re-narrow
 * here at the read site instead of leaking `any`.
 */
export interface JobStatusWithTicketMetadata {
  metadata?: Record<string, unknown> | null
}

export function extractUnknownRowsTicket(
  job: JobStatusWithTicketMetadata,
): UnknownRowsTicketBanner | null {
  const meta = job.metadata
  if (!meta || typeof meta !== 'object') return null
  const candidate = meta.unknownRowsTicket
  if (!candidate || typeof candidate !== 'object') return null
  const obj = candidate as Record<string, unknown>
  const ticketId = obj.ticketId
  const ticketReference = obj.ticketReference
  const reused = obj.reused
  const rawCount = obj.unknownRowCount
  // `unknownRowCount` accepts: a positive number (real count), `null` (count
  // indeterminate after staging prune — banner renders without a number),
  // or `undefined`/missing (treated as null). 0 or a
  // negative number is rejected because it means "no unknowns" and should
  // never come paired with a ticketId.
  const countIsInvalid =
    rawCount !== null && rawCount !== undefined && (typeof rawCount !== 'number' || rawCount <= 0)
  if (
    typeof ticketId !== 'string' ||
    typeof ticketReference !== 'string' ||
    typeof reused !== 'boolean' ||
    countIsInvalid
  ) {
    return null
  }
  const normalisedCount: number | null = typeof rawCount === 'number' ? rawCount : null
  return { ticketId, ticketReference, reused, unknownRowCount: normalisedCount }
}

/**
 * Fields that carry "how many rows this job BOOKED", most authoritative first.
 *
 * `transactionsCreated` leads because it is the one field every import path
 * writes with the number of rows that reached `bat.transactions`
 * (custom-import staging writer, `csv-import-finalize`), and because
 * `GET /imports/jobs/:id/status` always emits the whole family — defaulting
 * every absent one to `0`. Reading `recordsProcessed` first therefore returned
 * that DEFAULT rather than a real count: a custom import of 10 rows (whose
 * metadata carries `transactionsCreated: 10` and no `recordsProcessed`) was
 * confirmed to the user as `0 registros` while the toast said 10 and all 10
 * rows were in the ledger. The toast vanishes; the wrong ticket stays.
 *
 * `recordsProcessed`/`processedRecords` stay LAST as a fallback: they
 * count rows the pipeline READ, which is ≥ the rows it booked.
 */
const IMPORTED_RECORD_COUNT_FIELDS = [
  'transactionsCreated',
  'importedCount',
  'recordsImported',
  'processedRecords',
  'recordsProcessed',
] as const

/**
 * How many rows the finished job actually booked, when the API reports it.
 *
 * Returns null when the payload carries no usable count — the completion card
 * then shows no number rather than a fabricated `0 registros` (the exact
 * dishonesty this panel was reported for).
 */
export function extractImportedRecordCount(job: unknown): number | null {
  if (!job || typeof job !== 'object') return null
  const record = job as Record<string, unknown>
  for (const key of IMPORTED_RECORD_COUNT_FIELDS) {
    const value = record[key]
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value
  }
  return null
}
